import { Css, Icon, LoadingSkeleton, Palette, SelectField, TextField, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { useMemo, useState } from "react";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { Camera, LocationOption, Part } from "../types";

interface CameraSidebarProps {
  locations: LocationOption[];
  loadingLocations: boolean;
}

export const CameraSidebar = observer(function CameraSidebar({ locations, loadingLocations }: CameraSidebarProps) {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const camera = store.selectedCamera;

  return (
    <div {...tid.cameraSidebar}>
      {/* Room Assignment section at top */}
      {camera && (
        <div css={Css.px2.pt2.pb1.bb.bcGray200.$}>
          <RoomAssignment camera={camera} locations={locations} />
        </div>
      )}

      {/* Parts list */}
      <div css={Css.px1.py1.$}>
        {store.cameras
          .sortBy((c) => c.cameraName ?? "")
          .map((cam) => (
            <CameraSection key={cam.id} camera={cam} locations={locations} loadingLocations={loadingLocations} />
          ))}
      </div>
    </div>
  );
});

const CameraSection = observer(function CameraSection({
  camera,
  locations,
  loadingLocations,
}: {
  camera: Camera;
  locations: LocationOption[];
  loadingLocations: boolean;
}) {
  const store = useInsideMapsConfig();
  const isExpanded = store.selectedCameraId === camera.id;

  return (
    <div css={Css.mb1.$}>
      <div css={Css.df.aic.gap1.px1.pyPx(4).cursorPointer.smSb.gray700.$} onClick={() => store.toggleCamera(camera.id)}>
        <Icon icon={isExpanded ? "chevronDown" : "chevronRight"} inc={2} color={Palette.Gray700} />
        <span>{camera.cameraName || camera.displayName}</span>
      </div>
      {isExpanded && (
        <div css={Css.pl2.$}>
          {camera.parts
            .sortBy((p) => p.name ?? "")
            .map((part) => (
              <PartRow
                key={part.id}
                part={part}
                cameraId={camera.id}
                locations={locations}
                loadingLocations={loadingLocations}
              />
            ))}
        </div>
      )}
    </div>
  );
});

const RoomAssignment = observer(function RoomAssignment({
  camera,
  locations,
}: {
  camera: Camera;
  locations: LocationOption[];
}) {
  const store = useInsideMapsConfig();
  const [inputMode, setInputMode] = useState<"select" | "manual">("select");
  const [manualLocationId, setManualLocationId] = useState("");

  const uniqueRooms = useMemo(
    () => locations.uniqueBy((l) => l.roomId).map((l) => ({ id: l.roomId, name: l.roomName })),
    [locations],
  );

  const roomOptions = useMemo(() => uniqueRooms.map((r) => ({ id: r.id, name: r.name })), [uniqueRooms]);

  const selectedRoomName = uniqueRooms.find((r) => r.id === camera.locationId)?.name;
  const selectedLocation = locations.find((l) => l.roomId === camera.locationId);

  return (
    <div css={Css.xs.$}>
      {/* Room Assignment header */}
      <div css={Css.gray600.mb1.$}>Room Assignment:</div>
      <div css={Css.df.fdc.gap1.mb2.$}>
        <label css={Css.df.aic.gap1.cursorPointer.$}>
          <input type="radio" checked={!!camera.locationId} readOnly css={Css.add("accentColor", "#2563eb").$} />
          <span>Explicitly set for this camera</span>
        </label>
        <label css={Css.df.aic.gap1.cursorPointer.$}>
          <input type="radio" checked={!camera.locationId} readOnly />
          <span>Inherited from another camera</span>
        </label>
      </div>

      {/* Room info display */}
      {selectedRoomName && (
        <div css={Css.mb2.$}>
          <div css={Css.sm.gray700.$}>{selectedRoomName}</div>
          {selectedLocation && <div css={Css.green700.smSb.$}>{selectedLocation.name}</div>}
        </div>
      )}

      {/* Camera Room */}
      <div css={Css.gray600.mb1.$}>Camera Room</div>

      {/* Input Mode toggle */}
      <div css={Css.xs.gray600.mb1.$}>Input Mode:</div>
      <div css={Css.df.gap1.mb2.$}>
        <ModeButton
          label="Select from List"
          active={inputMode === "select"}
          onClick={() => setInputMode("select")}
          color="green"
        />
        <ModeButton
          label="Manual Input"
          active={inputMode === "manual"}
          onClick={() => setInputMode("manual")}
          color="green"
        />
      </div>

      {inputMode === "select" ? (
        <div>
          <SelectField
            label="Camera Room"
            labelStyle="hidden"
            value={camera.locationId ?? undefined}
            onSelect={(val) => store.setCameraLocationId(camera.id, val)}
            options={roomOptions}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
          />
        </div>
      ) : (
        <div css={Css.df.fdc.gap1.$}>
          <TextField
            label="Location ID"
            labelStyle="hidden"
            value={manualLocationId}
            onChange={(val) => setManualLocationId(val ?? "")}
            placeholder="Enter location ID"
          />
          <button
            css={Css.pyPx(4).pxPx(12).br4.sm.cursorPointer.bgBlue700.white.add("border", "none").$}
            onClick={() => {
              if (manualLocationId.trim()) {
                store.setCameraLocationId(camera.id, manualLocationId.trim());
                setManualLocationId("");
              }
            }}
          >
            Apply
          </button>
        </div>
      )}

      {camera.locationId && <div css={Css.mt1.xs.gray500.$}>Location ID: {camera.locationId}</div>}
    </div>
  );
});

const PartRow = observer(function PartRow({
  part,
  cameraId,
  locations,
  loadingLocations,
}: {
  part: Part;
  cameraId: string;
  locations: LocationOption[];
  loadingLocations: boolean;
}) {
  const store = useInsideMapsConfig();
  const isSelected = store.selectedCameraId === cameraId && store.selectedPartId === part.id;

  const hasMaterials = part.textures.some((t) => t.materialVariantIds?.nonEmpty);
  const hasLocations = part.locationIds?.nonEmpty;
  const allReviewed = part.textures.length > 0 && part.textures.every((t) => t.verified || t.skipped);

  // Get location names for display (matching checkout-tool behavior)
  const locationNames = useMemo(() => {
    if (!part.locationIds?.nonEmpty) return "No Locations";
    const locationMap = new Map(locations.map((l) => [l.id, l.name]));
    const names = part.locationIds.map((id) => locationMap.get(id)).compact();
    return names.length > 0 ? names.join(", ") : "No Locations";
  }, [part.locationIds, locations]);

  // Status icon
  let statusColor: Palette = Palette.Gray400;
  if (hasMaterials && hasLocations && allReviewed) {
    statusColor = Palette.Green600;
  } else if (hasMaterials) {
    statusColor = Palette.Yellow600;
  } else if (allReviewed) {
    statusColor = Palette.Blue600;
  }

  return (
    <div
      css={Css.df.aic.jcsb.pyPx(6).pxPx(8).cursorPointer.br4.sm.if(isSelected).bgYellow100.add("fontWeight", "700").$}
      onClick={() => {
        store.selectCamera(cameraId);
        store.selectPart(part.id);
      }}
    >
      <div css={Css.df.aic.gap1.add("minWidth", "0").$}>
        <Icon icon="circle" inc={1.5} color={statusColor} />
        <div css={Css.add("minWidth", "0").$}>
          <div css={Css.sm.if(isSelected).smSb.$}>{part.name}</div>
          {loadingLocations ? (
            <LoadingSkeleton rows={1} columns={1} />
          ) : (
            <div css={Css.xs.gray500.$}>{locationNames}</div>
          )}
        </div>
      </div>
      <button
        css={Css.cursorPointer.add("background", "none").add("border", "none").add("flexShrink", "0").$}
        onClick={(e) => {
          e.stopPropagation();
          // Copy part data to clipboard
          const partData = JSON.stringify(part, null, 2);
          void navigator.clipboard.writeText(partData);
        }}
        title="Copy part data"
      >
        <Icon icon="duplicate" inc={2} color={Palette.Gray400} />
      </button>
    </div>
  );
});

function ModeButton({
  label,
  active,
  onClick,
  color,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  color: "green" | "blue";
}) {
  const bgActive = color === "green" ? "#16a34a" : "#1d4ed8";
  return (
    <button
      css={
        Css.pyPx(4)
          .pxPx(10)
          .br4.xs.cursorPointer.if(active)
          .white.add("background", bgActive)
          .add("border", `1px solid ${bgActive}`)
          .if(!active)
          .gray700.bgWhite.add("border", "1px solid #d1d5db").$
      }
      onClick={onClick}
    >
      {label}
    </button>
  );
}
